/**
 * Expanded override view — inspector-style detail sheet consistent with the
 * other index pages (docs/expandable-index-pages.md). Overrides are read-only
 * (they come from markdown uploads), so there is no action group.
 */
import { useGetOne, useListContext, useRecordContext } from 'react-admin';
import { Box, Chip, Stack, Typography } from '@mui/material';

import { CopyButton, DetailRow, KeyFactsStrip, SectionLabel } from '@src/components/indexPage';
import { getCurrencySymbol } from '@src/utils/currency';
import type { PartnerSettings } from '@src/settings';

import type { YAdjustedRowWithId } from './types';

const formatCents = (value: number | null | undefined, symbol: string): string =>
  (value == null ? '—' : `${symbol}${(value / 100).toFixed(2)}`);

const ItemAdjustedPricesExpand = () => {
  const row = useRecordContext<YAdjustedRowWithId>();
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });

  if (!row) return null;

  const symbol = getCurrencySymbol(settings?.primaryCurrencyCode);
  // Set the value AND mark the filter displayed; otherwise a non-alwaysOn
  // filter (Color/Size/Markdown code) applies invisibly with no chip to clear.
  const filterLink = (field: string, value: string | null | undefined) =>
    (value ?
      () => setFilters({ ...filterValues, [field]: value }, { ...displayedFilters, [field]: true }) :
      undefined);

  const reduction = row.yerdlePrice && row.yadjustedPrice != null && row.yerdlePrice > 0 ?
    Math.round((1 - row.yadjustedPrice / row.yerdlePrice) * 100) :
    null;

  return (
    <Box sx={{ bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', p: 2.5 }}>
      <Stack spacing={2}>
        {/* Identity header */}
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontSize: 16, lineHeight: 1.3 }}>
              {row.title ?? row.parentSku ?? 'Item override'}
            </Typography>
            {row.yadjustedCode && <Chip size="small" variant="outlined" label={`Markdown ${row.yadjustedCode}`} />}
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'inline-flex', alignItems: 'center' }}>
            {row.parentSku ?? '—'}
            {row.parentSku && <CopyButton name="Parent SKU" value={row.parentSku} />}
            {row.skuSubcode ? ` · ${row.skuSubcode}` : ''}
          </Typography>
        </Box>

        <KeyFactsStrip
          facts={[
            { label: 'Base price', value: formatCents(row.price, symbol) },
            { label: 'Resale price', value: formatCents(row.yerdlePrice, symbol) },
            { label: 'Override', value: formatCents(row.yadjustedPrice, symbol) },
            ...(reduction != null ? [{ label: 'vs. resale', value: `−${reduction}%` }] : []),
          ]}
        />

        {/* Detail sections */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(250px, 1fr))' },
            columnGap: 4,
            rowGap: 2.5,
            alignContent: 'start',
          }}
        >
          <Box>
            <SectionLabel>Identifiers</SectionLabel>
            <DetailRow label="Parent SKU" value={row.parentSku} mono />
            <DetailRow label="SKU subcode" value={row.skuSubcode} mono />
            <DetailRow label="Markdown code" value={row.yadjustedCode} mono onClick={filterLink('yadjustedCode', row.yadjustedCode)} />
          </Box>
          <Box>
            <SectionLabel>Attributes</SectionLabel>
            <DetailRow label="Color" value={row.color} onClick={filterLink('color', row.color)} />
            <DetailRow label="Size" value={row.sizeCode} onClick={filterLink('sizeCode', row.sizeCode)} />
            <DetailRow label="Department" value={row.department} onClick={filterLink('department', row.department)} />
            <DetailRow label="Category" value={row.category} onClick={filterLink('category', row.category)} />
          </Box>
          <Box>
            <SectionLabel>Effect</SectionLabel>
            <DetailRow
              label="Override"
              value={row.yadjustedPrice != null ?
                `Storefront price is fixed at ${formatCents(row.yadjustedPrice, symbol)} while this markdown is active` :
                '—'}
            />
            <DetailRow
              label="Without it"
              value={row.yerdlePrice != null ?
                `Price rules would list this item at ${formatCents(row.yerdlePrice, symbol)}` :
                '—'}
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default ItemAdjustedPricesExpand;
